import * as React from 'react';
import { Link, Outlet, createFileRoute, redirect } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Activity, BarChart3, ChartPie, Database, Home, Mail, Menu, Newspaper, Search, Settings, Users, FolderLock, Building2, X } from 'lucide-react';
import { Wordmark } from '@client/components/brand/logo';
import { api, ApiError, qk } from '@client/lib/api';
import type { StaffMe } from '@client/lib/admin-api';
import { Button } from '@client/components/ui/button';
import { CommandPalette } from '@client/components/admin/command-palette';
import { Avatar } from '@client/components/investor/post-article';
import { cn } from '@client/lib/utils';

export const Route = createFileRoute('/admin')({
  beforeLoad: async ({ context }) => {
    try {
      const staff = await context.queryClient.fetchQuery({ queryKey: qk.admin('me'), queryFn: () => api<StaffMe>('/api/admin/me'), staleTime: 60_000 });
      return { staff };
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) throw redirect({ to: '/admin-denied' });
      throw err;
    }
  },
  component: AdminLayout,
});

const nav = [
  { section: 'Overview', items: [{ to: '/admin', label: 'admin.home', icon: Home, exact: true }] },
  { section: 'Investors', items: [{ to: '/admin/rooms', label: 'admin.rooms', icon: FolderLock }, { to: '/admin/updates', label: 'admin.updates', icon: Newspaper }, { to: '/admin/investors', label: 'admin.investors', icon: Users }, { to: '/admin/firms', label: 'Firms', icon: Building2, raw: true }, { to: '/admin/captable', label: 'Cap table', icon: ChartPie, raw: true }] },
  { section: 'Data', items: [{ to: '/admin/datasets', label: 'admin.datasets', icon: Database }, { to: '/admin/dashboards', label: 'admin.dashboards', icon: BarChart3 }] },
  { section: 'System', items: [{ to: '/admin/activity', label: 'admin.activity', icon: Activity }, { to: '/admin/outbox', label: 'admin.outbox', icon: Mail }, { to: '/admin/settings', label: 'admin.settings', icon: Settings }] },
] as const;

/** Staff app shell (RX context sidebar): sectioned nav with the gradient tick on the active item; ⌘K quick switcher. Stays light. */
function AdminLayout() {
  const { t } = useTranslation();
  const { staff } = Route.useRouteContext();
  const [menuOpen, setMenuOpen] = React.useState(false);
  React.useEffect(() => {
    const key = `rx_staff_login_${staff.id}_${new Date().toISOString().slice(0, 10)}`;
    try {
      if (!sessionStorage.getItem(key)) { sessionStorage.setItem(key, '1'); api('/api/admin/login-event', { method: 'POST' }).catch(() => undefined); }
    } catch { /* ignore */ }
  }, [staff.id]);
  const initials = staff.name.split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase();
  const links = (onPick?: () => void) => nav.map((s) => (
    <div key={s.section} className="mb-3">
      <p className="px-3 pb-1 text-[10px] font-[700] uppercase tracking-[0.08em] text-subtle">{s.section}</p>
      {s.items.map((n) => (
        <Link key={n.to} to={n.to} onClick={onPick} activeOptions={{ exact: 'exact' in n && n.exact }} className="group relative mb-0.5 flex items-center gap-2.5 rounded-[10px] px-3 py-[7px] text-[13px] font-[600] text-body no-underline transition-colors duration-150 hover:bg-row-hover hover:text-foreground" activeProps={{ className: cn('bg-soft text-link hover:bg-soft hover:text-link before:absolute before:left-0 before:top-1/2 before:h-4 before:w-[3px] before:-translate-y-1/2 before:rounded-r before:bg-gradient-brand') }}>
          <n.icon className="size-4" />
          {'raw' in n ? n.label : t(n.label)}
        </Link>
      ))}
    </div>
  ));
  return (
    <div className="flex min-h-dvh flex-col bg-background text-foreground md:flex-row" data-theme="light">
      {/* Phone/tablet: compact top bar with a slide-down menu; the full sidebar appears from md up. */}
      <header className="sticky top-0 z-[200] flex h-12 items-center justify-between border-b border-border bg-card px-4 md:hidden">
        <Link to="/admin" className="flex items-center gap-2 no-underline"><Wordmark width={110} /><span className="text-[10.5px] font-[700] uppercase tracking-[0.07em] text-muted">Admin</span></Link>
        <Button variant="ghost" size="icon-sm" aria-label={menuOpen ? 'Close menu' : 'Open menu'} aria-expanded={menuOpen} onClick={() => setMenuOpen((o) => !o)}>{menuOpen ? <X /> : <Menu />}</Button>
      </header>
      {menuOpen ? (
        <nav className="border-b border-border bg-card px-3 py-3 md:hidden" aria-label="Admin sections">
          {links(() => setMenuOpen(false))}
          <p className="truncate px-3 pb-1 pt-2 text-[12.5px] text-muted">{staff.name} · {staff.email} · {staff.role}</p>
        </nav>
      ) : null}
      <aside className="sticky top-0 hidden h-dvh w-[236px] shrink-0 flex-col border-r border-border bg-card md:flex">
        <div className="px-5 pb-3 pt-5">
          <Link to="/admin" className="no-underline"><Wordmark width={128} /></Link>
          <span className="mt-1.5 block text-[10.5px] font-[700] uppercase tracking-[0.07em] text-muted">Investor Room · Admin</span>
        </div>
        <button onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }))} className="mx-3 mb-3 flex h-8 items-center gap-2 rounded-btn bg-sunken px-3 text-[12.5px] text-muted hover:bg-row-active hover:text-foreground">
          <Search className="size-3.5" /><span className="flex-1 text-left">Jump to…</span><kbd className="mono text-[10.5px]">⌘K</kbd>
        </button>
        <nav className="rx-scroll flex-1 overflow-y-auto px-3">{links()}</nav>
        <div className="flex items-center gap-2.5 border-t border-border px-4 py-3">
          <Avatar initials={initials} size={30} />
          <div className="min-w-0">
            <p className="truncate text-[13px] font-[700] text-foreground">{staff.name}{staff.title ? <span className="font-[500] text-muted"> · {staff.title}</span> : null}</p>
            <p className="truncate text-[11.5px] text-muted">{staff.email} · {staff.role}</p>
          </div>
        </div>
      </aside>
      <main className="min-w-0 flex-1">
        <Outlet />
      </main>
      <CommandPalette />
    </div>
  );
}
